import React, { useState, useEffect, useCallback } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Plus, ClipboardList, User, Building2, Car, Search, CheckCircle2,
  Clock, AlertTriangle, Eye
} from 'lucide-react';
import { getOrdenesT, crearOrdenT, getEmpresas, getTecnicos, scraperPatente } from '../../lib/gestion4satApi';
import { toast } from 'sonner';

const tipoLabels = {
  instalacion: 'Instalaci\u00f3n', mantencion: 'Mantenci\u00f3n', asistencia: 'Asistencia',
  revision: 'Revisi\u00f3n', retiro: 'Retiro', otro: 'Otro',
};
const estadoColors = {
  asignada: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  en_progreso: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  completada: 'bg-green-500/10 text-green-600 border-green-500/20',
  cancelada: 'bg-red-500/10 text-red-600 border-red-500/20',
};
const prioColors = {
  alta: 'bg-red-500/10 text-red-600', normal: 'bg-gray-500/10 text-gray-600', baja: 'bg-blue-500/10 text-blue-500',
};

export default function G4sOrdenesTrabajoPage() {
  const [ordenes, setOrdenes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [tecnicos, setTecnicos] = useState([]);
  const [empresaSearch, setEmpresaSearch] = useState('');
  const [empresaResults, setEmpresaResults] = useState([]);

  const [form, setForm] = useState({
    tipo: 'instalacion', tecnico_id: '', empresa_id: '', empresa_razon: '',
    patente: '', imei: '', vehiculo_marca: '', vehiculo_modelo: '',
    direccion: '', notas: '', prioridad: 'normal', fecha_programada: '',
  });

  const load = useCallback(async () => {
    try {
      const d = await getOrdenesT({ estado: filtroEstado, limit: '100' });
      setOrdenes(d.ordenes);
      setTotal(d.total);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [filtroEstado]);

  useEffect(() => { load(); }, [load]);

  const openForm = async () => {
    try { const t = await getTecnicos(); setTecnicos(t.tecnicos); } catch { /* ignore */ }
    setForm({ tipo: 'instalacion', tecnico_id: '', empresa_id: '', empresa_razon: '', patente: '', imei: '', vehiculo_marca: '', vehiculo_modelo: '', direccion: '', notas: '', prioridad: 'normal', fecha_programada: '' });
    setShowForm(true);
  };

  const searchEmpresa = async (q) => {
    setEmpresaSearch(q);
    if (q.length < 2) { setEmpresaResults([]); return; }
    try { const d = await getEmpresas({ q, limit: 6 }); setEmpresaResults(d.empresas); } catch { /* ignore */ }
  };

  const selectEmpresa = (emp) => {
    setForm(f => ({ ...f, empresa_id: emp.id, empresa_razon: emp.razon_social }));
    setEmpresaSearch(''); setEmpresaResults([]);
  };

  const handlePatenteBlur = async () => {
    if (!form.patente || form.patente.length < 4 || form.vehiculo_marca) return;
    try {
      const r = await scraperPatente(form.patente);
      if (r.datos) setForm(f => ({ ...f, vehiculo_marca: r.datos.marca || '', vehiculo_modelo: r.datos.modelo || '' }));
    } catch { /* ignore */ }
  };

  const handleSubmit = async () => {
    if (!form.tecnico_id) { toast.error('Selecciona un t\u00e9cnico'); return; }
    try {
      await crearOrdenT(form);
      toast.success('Orden creada y notificaci\u00f3n enviada al t\u00e9cnico');
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <G4sLayout>
      <div className="space-y-4" data-testid="g4s-ordenes-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">&Oacute;rdenes de Trabajo</h1>
            <p className="text-sm text-muted-foreground">{total} &oacute;rdenes</p>
          </div>
          <Button onClick={openForm} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-nueva-orden">
            <Plus className="h-4 w-4 mr-2" /> Nueva Orden
          </Button>
        </div>

        <div className="flex gap-3">
          <Select value={filtroEstado} onValueChange={setFiltroEstado}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="asignada">Asignadas</SelectItem>
              <SelectItem value="en_progreso">En Progreso</SelectItem>
              <SelectItem value="completada">Completadas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          {loading ? Array.from({length:3}).map((_,i) => <Skeleton key={i} className="h-20 w-full" />) :
           ordenes.length === 0 ? (
            <Card><CardContent className="text-center py-12 text-muted-foreground">Sin &oacute;rdenes. Crea una para asignar trabajo a un t&eacute;cnico.</CardContent></Card>
          ) : ordenes.map(o => (
            <Card key={o.id} className="hover:border-emerald-500/20 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={estadoColors[o.estado] || ''}>{o.estado}</Badge>
                      <Badge variant="outline">{tipoLabels[o.tipo] || o.tipo}</Badge>
                      <Badge className={prioColors[o.prioridad] || ''}>{o.prioridad}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm mt-1">
                      <span className="flex items-center gap-1"><User className="h-3 w-3" />{o.tecnico_nombre}</span>
                      <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{o.empresa_razon_social || '-'}</span>
                      {o.patente_formateada && <span className="flex items-center gap-1 font-mono"><Car className="h-3 w-3" />{o.patente_formateada}</span>}
                    </div>
                    {o.notas_admin && <p className="text-xs text-muted-foreground mt-1">{o.notas_admin}</p>}
                    <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString('es-CL')} {o.fecha_programada && `| Programada: ${o.fecha_programada}`}</div>
                  </div>
                  {o.estado === 'completada' && o.servicio_id && (
                    <Badge className="bg-green-500/10 text-green-600"><CheckCircle2 className="h-3 w-3 mr-1" />Completada</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Create Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nueva Orden de Trabajo</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={v => setForm(f => ({...f, tipo: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(tipoLabels).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prioridad</Label>
                  <Select value={form.prioridad} onValueChange={v => setForm(f => ({...f, prioridad: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="baja">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>T&eacute;cnico *</Label>
                <Select value={form.tecnico_id} onValueChange={v => setForm(f => ({...f, tecnico_id: v}))}>
                  <SelectTrigger data-testid="select-tecnico"><SelectValue placeholder="Seleccionar t\u00e9cnico..." /></SelectTrigger>
                  <SelectContent>
                    {tecnicos.map(t => <SelectItem key={t.id} value={t.id}>{t.nombre} ({t.rol})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Empresa</Label>
                {form.empresa_id ? (
                  <div className="flex items-center justify-between p-2 rounded bg-emerald-500/10 border border-emerald-500/20 text-sm">
                    <span>{form.empresa_razon}</span>
                    <Button variant="ghost" size="sm" onClick={() => setForm(f => ({...f, empresa_id: '', empresa_razon: ''}))}>Cambiar</Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input placeholder="Buscar RUT o raz\u00f3n social..." value={empresaSearch} onChange={e => searchEmpresa(e.target.value)} />
                    {empresaResults.length > 0 && (
                      <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-card border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {empresaResults.map(emp => (
                          <button key={emp.id} onClick={() => selectEmpresa(emp)} className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b last:border-0">
                            <div className="font-medium">{emp.razon_social}</div>
                            <div className="text-xs text-muted-foreground font-mono">{emp.rut_formateado}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Patente</Label>
                  <Input value={form.patente} onChange={e => setForm(f => ({...f, patente: e.target.value.toUpperCase()}))} onBlur={handlePatenteBlur} placeholder="ABCD12" className="font-mono uppercase" />
                </div>
                <div>
                  <Label>IMEI (opcional, pre-disponer)</Label>
                  <Input value={form.imei} onChange={e => setForm(f => ({...f, imei: e.target.value}))} placeholder="T\u00e9cnico puede escanear QR" className="font-mono" />
                </div>
              </div>

              <div>
                <Label>Direcci&oacute;n del servicio</Label>
                <Input value={form.direccion} onChange={e => setForm(f => ({...f, direccion: e.target.value}))} />
              </div>

              <div>
                <Label>Fecha programada</Label>
                <Input type="date" value={form.fecha_programada} onChange={e => setForm(f => ({...f, fecha_programada: e.target.value}))} />
              </div>

              <div>
                <Label>Notas para el t&eacute;cnico</Label>
                <Textarea value={form.notas} onChange={e => setForm(f => ({...f, notas: e.target.value}))} placeholder="Instrucciones, contacto del cliente..." rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-crear-orden">Crear y Notificar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </G4sLayout>
  );
}
